import { useState } from "react";
import type { ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ConfirmDangerButtonProps {
  title: string;
  resourceName: string;
  resourceIdentifier?: string;
  confirmationText?: string;
  actionLabel: string;
  description?: string;
  disabled?: boolean;
  triggerVariant?: ButtonProps["variant"];
  triggerSize?: ButtonProps["size"];
  triggerClassName?: string;
  children: ReactNode;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDangerButton({
  title,
  resourceName,
  resourceIdentifier,
  confirmationText,
  actionLabel,
  description,
  disabled,
  triggerVariant = "ghost",
  triggerSize = "icon",
  triggerClassName,
  children,
  onConfirm,
}: ConfirmDangerButtonProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expectedText = confirmationText ?? resourceName;
  const matches = typed === expectedText;

  function close() {
    if (confirming) return;
    reset();
  }

  function reset() {
    setOpen(false);
    setTyped("");
    setCopied(false);
    setError(null);
  }

  async function copyConfirmationText() {
    try {
      await navigator.clipboard.writeText(expectedText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function confirm() {
    setError(null);
    setConfirming(true);
    try {
      await onConfirm();
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <>
      <Button
        variant={triggerVariant}
        size={triggerSize}
        className={triggerClassName ?? "text-destructive hover:text-destructive"}
        title={title}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {children}
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
          <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-lg">
            <div className="space-y-2">
              <h2 className="text-base font-semibold">{title}</h2>
              <p className="text-sm text-muted-foreground">
                {description ?? "This action cannot be undone."}
              </p>
            </div>

            <div className="mt-4 space-y-2">
              <div className="space-y-1.5">
                <Label htmlFor="danger-resource-name">Resource name</Label>
                <div className="flex gap-2">
                  <Input
                    id="danger-resource-name"
                    value={resourceName}
                    readOnly
                    className="font-mono text-xs"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <Button type="button" variant="outline" size="icon" title="Copy confirmation text" onClick={copyConfirmationText}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {resourceIdentifier && (
                <div className="space-y-1.5">
                  <Label htmlFor="danger-resource-id">Resource ID</Label>
                  <Input
                    id="danger-resource-id"
                    value={resourceIdentifier}
                    readOnly
                    className="font-mono text-xs"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </div>
              )}
              <Label htmlFor="danger-confirm">
                Type <span className="font-mono text-foreground">{expectedText}</span> to confirm
              </Label>
              <Input
                id="danger-confirm"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") close();
                }}
                disabled={confirming}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={close} disabled={confirming}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={!matches || disabled || confirming}
                onClick={confirm}
              >
                {confirming ? "Working…" : actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
